import { type BaseErrorOptions } from '@canvas/error';

import { type DelegatingClient } from '../delegating-client-types.ts';
import { DelegatingGraphQLClient } from '../delegating-graphql-client.ts';
import { GraphQLClientError, throwOnQueryError } from '../graphql-response.ts';

import * as UnitsQueryUnitSettings from './queries/units-query-unit-settings.graphql';
import {
  type UnitSettings,
  type UnitSettingsQuery,
  type UnitSettingsQueryInput,
} from './units-client-types.ts';

export class UnitsGraphqlFetchError extends GraphQLClientError {
  constructor(message: string, options?: BaseErrorOptions) {
    super(message, { name: 'UnitsGraphqlFetchError', ...options });
  }
}

/**
 * Reads Collaboration Context unit rollout settings from the AGG GraphQL gateway.
 */
export class UnitsClient {
  private readonly clients: DelegatingClient = new DelegatingGraphQLClient();
  private readonly origin = new URL(
    globalThis.location ? globalThis.location.origin : '',
  );

  private getBaseUrl() {
    const base = this.origin.toString();
    if (base.endsWith('/')) {
      return base.slice(0, -1);
    }

    return base;
  }

  /**
   * Load the units rollout settings for an organisation.
   *
   * @param orgId The organisation to read settings for
   * @returns The org's unit settings, or undefined when the org has none
   */
  public async loadUnitSettings(orgId: string): Promise<UnitSettings | undefined> {
    const client = this.clients.getOrCreate(this.getBaseUrl(), '/gateway/api/graphql');

    const responseOrErr = await client
      .query<UnitSettingsQuery['data'], UnitSettingsQueryInput>({
        query: UnitsQueryUnitSettings.UnitSettings,
        variables: { orgId },
      })
      // To catch runtime/network errors
      .catch((err: Error): Error => new UnitsGraphqlFetchError(err.message));

    // To catch GraphQL errors
    throwOnQueryError<UnitSettingsQuery['data']>(responseOrErr);

    return responseOrErr.data.admin_unitSettings ?? undefined;
  }
}

let unitsClient: UnitsClient;

export const getUnitsClient = () => {
  if (!unitsClient) {
    unitsClient = new UnitsClient();
  }

  return unitsClient;
};
